/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthCard } from 'src/components/AuthCard/AuthCard';
import { ThemeToggle } from 'src/components/common/ThemeToggle';
import logoImg from 'src/assets/images/logo.png';
import './AuthLayout.css';

const AuthLayout = () => {
  const [activeTab, setActiveTab] = useState('Create account');
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fabVisible, setFabVisible] = useState(false);
  const heroContentRef = useRef(null);
  const authRef = useRef(null);

  useEffect(() => {
    if (location.pathname === '/login') {
      setActiveTab('Sign in');
    }
  }, [location.pathname]);

  useEffect(() => {
    // Debug aid for "login page unclickable" issues (usually an overlay / body pointer-events).
    if (location.pathname !== '/login') return;

    const logBlocker = (evt) => {
      try {
        const x = evt?.touches?.[0]?.clientX ?? evt?.clientX ?? window.innerWidth / 2;
        const y = evt?.touches?.[0]?.clientY ?? evt?.clientY ?? window.innerHeight / 2;
        const stack = document.elementsFromPoint?.(x, y) || [];
        console.debug('[login-debug] interaction', {
          type: evt.type,
          target: evt.target?.tagName,
          bodyPointerEvents: document?.body?.style?.pointerEvents,
          bodyOverflow: document?.body?.style?.overflow,
          topElement: stack?.[0]?.tagName,
          topElementId: stack?.[0]?.id,
          topElementClass: stack?.[0]?.className,
        });
      } catch (e) {
        console.debug('[login-debug] interaction log failed', e);
      }
    };

    console.debug('[login-debug] mounted', {
      bodyPointerEvents: document?.body?.style?.pointerEvents,
      bodyOverflow: document?.body?.style?.overflow,
    });

    window.addEventListener('click', logBlocker, true);
    window.addEventListener('touchstart', logBlocker, true);
    return () => {
      window.removeEventListener('click', logBlocker, true);
      window.removeEventListener('touchstart', logBlocker, true);
    };
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
      if (
        window.innerWidth <= 768 &&
        heroContentRef.current &&
        authRef.current
      ) {
        const hb = heroContentRef.current.getBoundingClientRect().bottom;
        const ct = authRef.current.getBoundingClientRect().top;
        const wh = window.innerHeight;
        setFabVisible(hb < wh * 0.5 && ct > wh * 0.7);
      } else {
        setFabVisible(false);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const scrollToAuth = useCallback(() => {
    setMenuOpen(false);
    authRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return (
    <div className="tb-page">
      {/* ── NAV ── */}
      <nav className={`tb-nav${scrolled ? ' scrolled' : ''}`}>
        <div className="tb-nav-inner">
          <a
            className="tb-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <div className="tb-logo-icon">
              <img src={logoImg} alt="Logo" />
            </div>
            AHEDGE
          </a>
          <ThemeToggle className="tb-theme-toggle" />
          {/* <div className="tb-nav-links">
            <a className="tb-nav-cta" onClick={scrollToAuth}>
              Get Started
            </a>
          </div>
          <button
            className="tb-hamburger"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              {menuOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button> */}
        </div>
      </nav>

      {/* ── MOBILE MENU ── */}
      <div className={`tb-mobile-menu${menuOpen ? ' open' : ''}`}>
        <a
          className="tb-nav-cta-m"
          onClick={scrollToAuth}
        >
          Get Started
        </a>
      </div>

      {/* ── MOBILE FAB ── */}
      <button
        className={`tb-mobile-fab${fabVisible ? ' visible' : ''}`}
        onClick={scrollToAuth}
      >
        Create Account
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <polyline points="19 12 12 19 5 12" />
        </svg>
      </button>

      {/* ── HERO ── */}
      <section className="tb-hero">
        <div className="tb-hero-inner">
          {/* Left: Hero Content */}
          <div className="tb-hero-content" ref={heroContentRef}>
            <div className="tb-hero-badge">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: '#7dd3fc', flexShrink: 0 }}
              >
                <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" />
              </svg>
              AI-powered resume matching
            </div>

            <h1 className="font-semibold">
              Launch your{' '}
              <span>Risk &amp; Compliance</span>
              {' '}career
            </h1>

            <p className="tb-hero-desc">
              Your fast track into risk management and regulatory compliance roles at top banks and fintechs.
            </p>

            {/* Feature Pills */}
            {/* <div className="tb-hero-pills">
              <div className="tb-hero-pill">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                Mentor-led sessions
              </div>
              <div className="tb-hero-pill">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Verified Companies
              </div>
              <div className="tb-hero-pill">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                  <polyline points="16 7 22 7 22 13" />
                </svg>
                Career Guidance
              </div>
            </div> */}

            {/* Feature Cards */}
            {/* <div className="tb-feature-cards">
              <div className="tb-feature-card">
                <div className="tb-feature-card-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" />
                  </svg>
                </div>
                <span>AI profile analysis</span>
              </div>
              <div className="tb-feature-card">
                <div className="tb-feature-card-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                </div>
                <span>Micro-Bootcamps</span>
              </div>
              <div className="tb-feature-card">
                <div className="tb-feature-card-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87" />
                    <path d="M16 3.13a4 4 0 010 7.75" />
                  </svg>
                </div>
                <span>Mentor-led sessions</span>
              </div>
            </div> */}
          </div>

          {/* Right: Auth Card */}
          <div className="tb-auth-float" ref={authRef}>
            <AuthCard activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="tb-footer">
        <div className="tb-footer-inner">
          <p>&copy; {new Date().getFullYear()} AHEDGE. All rights reserved.</p>
          <div className="tb-footer-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="mailto:support@ahedge.com">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthLayout;
